import { useCallback, useEffect, useState } from 'react';
import { GamesHome } from './components/GamesHome.tsx';
import { GamesLeaderboard } from './components/GamesLeaderboard.tsx';
import { SignInLanding } from './components/SignInLanding.tsx';
import { ROUTES } from './config.ts';
import { api, type HomeData } from './services/api.ts';

type BootState = 'loading' | 'signed-out' | 'ready' | 'unavailable';

function LoadingPage() {
  return <main className="page-loading" aria-label="Loading games"><span className="loading-logo" /><span className="loading-heading" /><span className="loading-card" /></main>;
}

function Unavailable({ retry }: { retry: () => void }) {
  return <main className="service-unavailable"><h1>Games are taking a break.</h1><p>Your account is still signed in. We just could not load the games service.</p><button type="button" onClick={retry}>Try again</button></main>;
}

export function App() {
  const [home, setHome] = useState<HomeData | null>(null);
  const [state, setState] = useState<BootState>('loading');
  const [path, setPath] = useState(window.location.pathname);

  const loadHome = useCallback(async () => {
    setState('loading');
    try {
      const session = await api.session();
      if (!session) { setHome(null); setState('signed-out'); return; }
      setHome(await api.home());
      setState('ready');
    } catch {
      setState('unavailable');
    }
  }, []);

  useEffect(() => { void loadHome(); }, [loadHome]);
  useEffect(() => {
    const pop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', pop);
    return () => window.removeEventListener('popstate', pop);
  }, []);

  const navigate = useCallback((next: string) => {
    if (next === ROUTES.smartDrive) { window.location.href = next; return; }
    window.history.pushState(null, '', next);
    setPath(next);
    window.scrollTo({ top: 0 });
  }, []);

  const signIn = useCallback(async (credential: string) => {
    await api.google(credential);
    setHome(await api.home());
    setState('ready');
  }, []);

  if (state === 'loading') return <LoadingPage />;
  if (state === 'unavailable') return <Unavailable retry={() => void loadHome()} />;
  if (state === 'signed-out' || !home) return <SignInLanding onCredential={signIn} />;
  if (path.startsWith(ROUTES.leaderboard)) return <GamesLeaderboard home={home} navigate={navigate} />;
  return <GamesHome home={home} navigate={navigate} />;
}
